import React, { useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import useWindowDimensions from '../../services/useWindowDimensions';
import chevronIcon from '../../assets/images/chevron.png';
import './ContactUs.css';
import Iframe from 'react-iframe'
import RingLoader from "react-spinners/RingLoader";
import { useAffiliateLink } from '../../hooks/useAffiliateLink';
import { appendAffiliateToUrl } from '../../utils/affiliateLink';

export default function ContactUs() {
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileNav, setMobileNav] = useState(false);
    const { height, width } = useWindowDimensions();
    const [loading, setLoading] = useState(true);
    const formSectionRef = useRef(null);

    const queryIntent = new URLSearchParams(location.search).get('intent');
    const [selectedIntent, setSelectedIntent] = useState(queryIntent === 'host' ? 'host' : 'advertiser');
    const affiliateCode = useAffiliateLink();

    const processSteps = useMemo(() => {
        if (selectedIntent === 'host') {
            return [
                {
                    title: 'Submit Form',
                    detail: 'Tell us about your venue, location, and goals in a short intake.'
                },
                {
                    title: 'Contact Within 24 Hours',
                    detail: 'An INVERT specialist follows up quickly to confirm fit and next steps.'
                },
                {
                    title: 'Schedule Installation',
                    detail: 'We coordinate timing and placement to keep your workflow smooth.'
                },
                {
                    title: 'Screen Installation & Setup',
                    detail: 'Hardware is installed, programmed, and prepared for launch.'
                },
                {
                    title: 'Go Live In The Network',
                    detail: 'Your location starts serving approved campaigns inside the INVERT network.'
                },
                {
                    title: 'Grow Local Presence',
                    detail: 'Your storefront becomes part of a stronger neighborhood visibility engine.'
                }
            ];
        }

        return [
            {
                title: 'Submit Campaign Inquiry',
                detail: 'Share your campaign goals, audience, and timeline in a short intake.'
            },
            {
                title: 'Contact Within 24 Hours',
                detail: 'An INVERT specialist follows up with recommended local screen zones.'
            },
            {
                title: 'Choose Locations & Budget',
                detail: 'Align on neighborhoods, venue clusters, budget range, and launch timing.'
            },
            {
                title: 'Creative Setup & Approval',
                detail: 'Finalize creative assets and make sure placement is launch-ready.'
            },
            {
                title: 'Campaign Goes Live',
                detail: 'Your ads begin running in real places where local attention is strongest.'
            },
            {
                title: 'Track & Optimize',
                detail: 'Review results, adjust targeting, and keep building local momentum.'
            }
        ];
    }, [selectedIntent]);

    const preparationItems = useMemo(() => {
        if (selectedIntent === 'host') {
            return [
                'Your business address and venue type.',
                'Preferred installation windows and operating hours.',
                'Any placement constraints we should know before setup.'
            ];
        }

        return [
            'Your campaign objective and target neighborhoods.',
            'Budget range and ideal campaign timeline.',
            'Creative format details or upcoming promotion dates.'
        ];
    }, [selectedIntent]);

    const expectationItems = useMemo(() => {
        if (selectedIntent === 'host') {
            return [
                'Fast follow-up from the INVERT team within 24 hours.',
                'A clear installation and onboarding recommendation.',
                'A simple launch path with minimal operational overhead.'
            ];
        }

        return [
            'Fast follow-up from the INVERT team within 24 hours.',
            'A clear local placement recommendation based on your goals.',
            'A practical campaign launch plan you can execute quickly.'
        ];
    }, [selectedIntent]);

    const mondayFormUrl = useMemo(() => {
        const baseUrl = 'https://forms.monday.com/forms/embed/3a988ea50ec55dee31a268cce9202c10?r=use1';
        const source = selectedIntent === 'host' ? 'host-flow' : 'advertiser-flow';
        const url = `${baseUrl}&intent=${selectedIntent}&source=${source}`;
        return appendAffiliateToUrl(url, affiliateCode);
    }, [selectedIntent, affiliateCode]);

    const handleIntentSelection = (intent) => {
        setSelectedIntent(intent);
        window.requestAnimationFrame(() => {
            formSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    };

   
    
    return (
        <div className='contactUsPage'>
            {width > 706 &&
                <div className='contactUsNav'>
                    <button className='contactUsNavLink' onClick={() => { navigate('/contactUs') }}>Contact Us</button>
                    <button className='contactUsNavTitle' onClick={() => navigate('/')}>INVERT</button>
                    <button className='contactUsNavLink' onClick={() => navigate('/userLogin')}>Login</button>
                </div>
            }

            {width <= 706 &&
                <div className='contactUsMobileNav'>
                    <div className='contactUsNav'>
                        <button className='contactUsNavTitle contactUsMobileTitle' onClick={() => navigate('/')}>INVERT</button>
                        <button className='contactUsChevronButton' onClick={() => setMobileNav(!mobileNav)}>
                            <img className='chevronIconnav' src={chevronIcon} alt='Open navigation menu' />
                        </button>
                    </div>
                    <div className='contactUsMobileMenu' style={{ display: (mobileNav && 'block') || 'none' }}>
                        <button className='contactUsMobileMenuItem' onClick={() => { navigate('/contactUs') }}>Contact Us</button>
                        <button className='contactUsMobileMenuItem' onClick={() => navigate('/userLogin')}>Login</button>
                    </div>
                </div>
            }

            <main className='contactUsContent'>
                <section className='contactUsIntro'>
                    <p className='contactUsIntroKicker'>The AI Operating System for Screens</p>
                    <h1>
                        {selectedIntent === 'host'
                            ? 'Get started with intelligent displays'
                            : 'Reach audiences on intelligent screens'}
                    </h1>
                    <p>
                        {selectedIntent === 'host'
                            ? 'Install INVERT, type what you want to display, and let AI handle the rest — from design to scheduling to passive ad revenue.'
                            : 'Launch campaigns across AI-powered screens in premium local venues with fast setup and market-level targeting.'}
                    </p>

                    <div className='contactIntentSelector'>
                        <button
                            className={`contactIntentButton ${selectedIntent === 'advertiser' ? 'contactIntentButtonActive' : ''}`}
                            onClick={() => handleIntentSelection('advertiser')}
                        >
                            I Want To Advertise
                        </button>
                        <button
                            className={`contactIntentButton ${selectedIntent === 'host' ? 'contactIntentButtonActive' : ''}`}
                            onClick={() => handleIntentSelection('host')}
                        >
                            I Want To Host A Screen
                        </button>
                    </div>
                </section>

                <section className='contactUsProcessTimeline'>
                    {processSteps.map((step, index) => (
                        <article className='contactProcessStep' key={step.title}>
                            <span className='contactProcessNumber'>{index + 1}</span>
                            <h3>{step.title}</h3>
                            <p>{step.detail}</p>
                        </article>
                    ))}
                </section>

                <section className='contactUsExpectationPanel'>
                    <div>
                        <h2>What To Prepare</h2>
                        <ul>
                            {preparationItems.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <h2>What You Can Expect</h2>
                        <ul>
                            {expectationItems.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                    </div>
                </section>

                <section className='contactUsFormSection' ref={formSectionRef}>
                    <div className='contactUsFormHeader'>
                        <h2>Submit Your Contact Form</h2>
                        <p>
                            You are currently submitting as: <strong>{selectedIntent === 'host' ? 'Host' : 'Advertiser'}</strong>
                        </p>
                    </div>

                    <div className='contactUsIframeWrap'>
                        <Iframe
                            onLoad={() => { setLoading(false) }}
                            loading='eager'
                            className='contactUsIframe'
                            url={mondayFormUrl}
                            width='100%'
                            height={Math.max(height - 130, 720)}
                            styles={{ border: 0 }}
                        />
                    </div>
                </section>
            </main>

            {loading &&
                <div className='contactUsSpinnerContainer'>
                    <RingLoader
                        color={'rgb(172, 172, 172)'}
                        loading={loading}
                        size={80}
                        aria-label='Loading Spinner'
                        data-testid='loader'
                    />
                </div>
            }
        </div>
      
  )
}
